/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { useMsal } from '@azure/msal-react';
import useMsalFunctions from '../util/useMsalAuthFunctions';
import {
  Box,
  CircularProgress,
  Link,
  Stack,
  StackProps,
  SvgIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FarmlandsLogoColored from '../assets/images/FarmlandsProLogoColored.svg';
import Typography from '../components/Typography';
import { Button } from '../components/Button';
import { colors } from '@farmlands/mui-theme';
import MaterialSymbol from '../components/MaterialSymbol';
import TestIds from '../testIds';
import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { URLS } from '../util/consts';

const featureIcons = [
  {
    icon: (
      <SvgIcon>
        <svg
          width='22'
          height='28'
          viewBox='0 0 22 28'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M11.9187 25.0863L0.49707 13.6646V15.998C0.49707 16.6163 0.74207 17.2113 1.1854 17.643L10.2737 26.7313C11.1837 27.6413 12.6654 27.6413 13.5754 26.7313L20.8204 19.4863C21.7304 18.5763 21.7304 17.0946 20.8204 16.1846L11.9187 25.0863Z'
            fill='#00553D'
          />
          <path
            d='M10.2737 20.898C10.7287 21.353 11.3237 21.5863 11.9187 21.5863C12.5137 21.5863 13.1087 21.353 13.5637 20.898L20.8087 13.653C21.7187 12.743 21.7187 11.2613 20.8087 10.3513L11.7204 1.26297C11.2887 0.831304 10.6937 0.586304 10.0754 0.586304H2.8304C1.54707 0.586304 0.49707 1.6363 0.49707 2.91964V10.1646C0.49707 10.783 0.74207 11.378 1.1854 11.8096L10.2737 20.898ZM2.8304 2.91964H10.0754L19.1637 12.008L11.9187 19.253L2.8304 10.1646V2.91964Z'
            fill='#00553D'
          />
          <path
            d='M5.4554 7.00297C6.26082 7.00297 6.91374 6.35005 6.91374 5.54464C6.91374 4.73922 6.26082 4.0863 5.4554 4.0863C4.64999 4.0863 3.99707 4.73922 3.99707 5.54464C3.99707 6.35005 4.64999 7.00297 5.4554 7.00297Z'
            fill='#00553D'
          />
        </svg>
      </SvgIcon>
    ),
    label: 'View your pricing',
  },
  { icon: 'star', label: 'Exclusive\n offers' },
  { icon: 'contract', label: 'Manage and track orders' },
  { icon: 'shopping_basket', label: 'Shop the full range' },
  { icon: 'distance', label: 'Manage multiple farms' },
  { icon: 'note', label: 'Sale Agreements' },
];

export default function Login() {
  const { handleMsalLogin, resetMsalInstance } = useMsalFunctions();
  const { inProgress } = useMsal();
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('md'));
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));

  const loginSectionStackProps = React.useMemo<StackProps>(() => {
    const defaultStyleProps: StackProps = {
      direction: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      flex: 4,
      px: 4,
      py: 4,
      bgcolor: '#F6F8F4',
    };

    if (isSm) {
      return {
        ...defaultStyleProps,
        bgcolor: '#ffffff',
        borderRadius: '24px 24px 0px 0px',
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        boxShadow: '8px 0px 16px 0px #0000001A',
      };
    }

    if (isXl) {
      // when screen get largest, move content more into center. This is workaround.
      return {
        ...defaultStyleProps,
        alignItems: 'flex-start',
        paddingLeft: '160px',
      };
    }

    return defaultStyleProps;
  }, [isSm, isXl]);

  const contentSectionStackProps = React.useMemo<StackProps>(() => {
    const defautlStackProps: StackProps = {
      alignItems: 'center',
    };

    if (isXl) {
      return {
        ...defautlStackProps,
        pr: '160px',
        alignItems: 'flex-end',
      };
    }

    return defautlStackProps;
  }, [isXl]);

  const handleRegister = () => {
    if (!process.env.REACT_APP_AZURE_B2C_REGISTER_REDIRECT_URI) {
      console.error('undefined REACT_APP_AZURE_B2C_REGISTER_REDIRECT_URI');
      return;
    }

    if (Capacitor.isNativePlatform()) {
      Browser.open({
        url: process.env.REACT_APP_AZURE_B2C_REGISTER_REDIRECT_URI,
        toolbarColor: '#004733',
      });
    } else {
      window.location.href = process.env.REACT_APP_AZURE_B2C_REGISTER_REDIRECT_URI;
    }
  };

  const handleFindOutMore = () => {
    if (Capacitor.isNativePlatform()) {
      Browser.open({
        url: URLS.becomeShareHolder,
        toolbarColor: '#004733',
      });
    } else {
      window.location.href = URLS.becomeShareHolder;
    }
  };

  return (
    <Box position='relative'>
      <Stack
        direction={{ sm: 'column', md: 'row' }}
        // Device safe area for mobile
        height={'var(--doc-height)'}
        pt={'var(--safe-area-statusbarHeight)'}
        pb={isSm ? '250px' : '0px'}
        overflow='auto'
      >
        <Stack direction='column' flex={7}>
          <Stack
            direction='column'
            justifyContent='center'
            flex={7}
            p={4}
            {...contentSectionStackProps}
          >
            <Stack direction='column' maxWidth={650} gap={4}>
              <img
                src={FarmlandsLogoColored}
                alt='Farmlands PRO'
                style={{
                  height: '25px',
                  alignSelf: 'flex-start',
                  display: isSm ? 'initial' : 'none',
                }}
              />

              <Box>
                <Typography
                  variant={isSm ? 'body1' : 'subtitle1'}
                  fontWeight='bold'
                  color='primary500'
                >
                  Enjoy the benefits of FarmlandsPRO
                </Typography>

                <Typography variant='body2' style={{ color: '#000000' }}>
                  Designed for New Zealand farmers and growers, FarmlandsPRO is your go-to tool to
                  order, track and manage your rural supplies.
                </Typography>
              </Box>

              <Stack direction='row' flexWrap='wrap' justifyContent='space-between' rowGap={4}>
                {featureIcons.map((i) => (
                  <FeatureIcon key={i.label} icon={i.icon} label={i.label} />
                ))}
              </Stack>

              <Typography variant='body2' style={{ color: '#000000' }}>
                It's easy to get started - FarmlandsPRO uses your MyFarmlands log in details, so
                have your username and password handy.
              </Typography>

              <Typography variant='body2' style={{ color: '#000000' }}>
                If you do not have a MyFarmlands account, click&nbsp;
                <Link onClick={handleRegister}>register now</Link>
              </Typography>
            </Stack>
          </Stack>
          <Stack
            direction='column'
            justifyContent='center'
            flex={3}
            bgcolor='#00553D'
            p={4}
            {...contentSectionStackProps}
          >
            <Stack direction='column' maxWidth={650} gap={4}>
              <Stack direction='column' gap={1}>
                <Typography variant='body1' fontWeight='bold' style={{ color: '#ffffff' }}>
                  Become a Shareholder
                </Typography>

                <Typography variant='body2' style={{ color: '#ffffff' }}>
                  To use FarmlandsPRO you need to be a Farmlands Shareholder and set up a
                  MyFarmlands account.
                </Typography>

                <Box>
                  <Typography variant='body2' style={{ color: '#ffffff' }}>
                    Being a Shareholder gives you
                  </Typography>
                  <ul style={{ ...theme.typography.body2, color: '#ffffff', paddingLeft: '20px' }}>
                    <li>Access better prices enabled by the buying power of our co-operative</li>
                    <li>Rural expertise from the Farmlands team</li>
                    <li>Discounts and rebates from over 7,000 Card Partner locations nationwide</li>
                    <li>One consolidated bill each month</li>
                  </ul>
                </Box>
              </Stack>

              <Box>
                <Button
                  variant='outlined'
                  sx={{
                    color: '#ffffff',
                    width: isSm ? '100%' : 'auto',
                    minWidth: '300px',
                    '&:hover': {
                      color: '#000',
                    },
                  }}
                  onClick={handleFindOutMore}
                >
                  Find out more
                </Button>
              </Box>
            </Stack>
          </Stack>
        </Stack>

        <Stack {...loginSectionStackProps}>
          <Stack direction='column' maxWidth='358px'>
            <img
              src={FarmlandsLogoColored}
              alt='Farmlands PRO'
              style={{
                height: '40px',
                alignSelf: 'flex-start',
                display: isSm ? 'none' : 'initial',
                marginBottom: isSm ? 26 : 48,
              }}
            />
            <Typography variant='body1' style={{ color: '#000000' }}>
              Use your MyFarmlands account details to log into FarmlandsPRO
            </Typography>
            <Box height={32} />
            {inProgress === 'login' ||
            inProgress === 'logout' ||
            inProgress === 'startup' ||
            inProgress === 'handleRedirect' ? (
              <Stack width='100%' gap={'12px'} justifyContent='center'>
                <Box margin='auto'>
                  <CircularProgress color='secondary' />
                </Box>
                <Button
                  variant='text'
                  onClick={() => {
                    resetMsalInstance();
                    window.location.reload();
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            ) : (
              <Stack direction='column' gap={2}>
                <Button
                  variant='contained'
                  data-testid={TestIds.feat.splash.loginButton}
                  onClick={() => handleMsalLogin()}
                >
                  Log in
                </Button>
                <Button
                  variant={isSm ? 'text' : 'outlined'}
                  data-testid={TestIds.feat.splash.registerButton}
                  onClick={handleRegister}
                >
                  Register Now
                </Button>
              </Stack>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
}

interface FeatureIconProps {
  icon: string | JSX.Element;
  label: string;
}

const FeatureIcon: React.FC<FeatureIconProps> = ({ icon, label }) => {
  return (
    <Stack
      direction='column'
      justifyContent='center'
      alignItems='center'
      maxWidth={'100px'}
      gap={1}
    >
      <Box
        width={64}
        height={64}
        bgcolor='#EAEFE6'
        display='flex'
        justifyContent='center'
        alignItems='center'
        borderRadius={64 / 2}
      >
        <MaterialSymbol sx={{ fontSize: '29px', color: colors.primary500 }}>{icon}</MaterialSymbol>
      </Box>
      <Typography
        variant='caption'
        whiteSpace='pre-line'
        color='primary500'
        textAlign='center'
        sx={{ height: '46px' }}
      >
        {label}
      </Typography>
    </Stack>
  );
};
